import React, { useState,useEffect,useCallback,useRef } from "react";
import { useParams,useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ColorPicker from "../../../components/ColorPicker";
import { getTemplateDetail, submitSingle, getSingleDetail, updateSingle, deleteSingle} from "../../../utils/spinningWheelApi";

const ContentTab = ({ isTemplateId,setTemplateId,updateSpinningWheelTabData,spinningWheelTabData,setRefreshWidgetList }) => {
    const [activeSettingElementsTab, setActiveSettingElementsTab] = useState("main");
    const [isLoading, setIsLoading] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRefs = useRef([]);
    const [apiProfileSociaMediaIcons, setApiProfileSociaMediaIcons] = useState([]);
    const [isSubmitSingle, setIsSubmitSingle] = useState(false); 
    const location = useLocation();
    const isEditPage = location.pathname.startsWith("/customer/edit/spinning-wheel/");
    const { uuid } = useParams();
    const templateId = isEditPage && uuid ? uuid : isTemplateId || "";
    const [apiSingle, setApiSingle] = useState(null);
    const [singleId, setSingleId] = useState(null);

    //details
    const [chanceSize, setChanceSize] = useState("20");
    const [customColor, setCustomColor] = useState("#000");
    const [heading, setHeading] = useState(spinningWheelTabData.heading);
    const [description, setDescription] = useState(spinningWheelTabData.description);
    const [isShowEmail, setIsShowEmail] = useState(Number(spinningWheelTabData.is_show_email));
    const [footerText, setFooterText] = useState(spinningWheelTabData.footer_text);

    //Wrap fetchTemplateDetails in useCallback to prevent re-creation on every render
    const fetchTemplateDetails = useCallback(async () => {
        if (!templateId) return;

        setIsLoading(true);
        try {
            const templateResult = await getTemplateDetail(templateId);
            if (templateResult?.status === true && Array.isArray(templateResult?.data?.data_list)) {
                if (templateResult?.status === true && templateResult?.data?.data_list) {
                    setApiProfileSociaMediaIcons(templateResult.data.data_list);
                } else {
                    setApiProfileSociaMediaIcons([]);
                }
            } else {
                setApiProfileSociaMediaIcons([]);
            }
        } catch (error) {
            //console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [templateId]);

    // Fix useEffect by including fetchTemplateDetails
    useEffect(() => {
        fetchTemplateDetails();
    }, [fetchTemplateDetails]);

    // Toggle dropdown visibility
    const toggleDropdown = (index) => {
        setOpenDropdown((prev) => (prev === index ? null : index));
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                openDropdown !== null &&
                dropdownRefs.current[openDropdown] &&
                !dropdownRefs.current[openDropdown].contains(event.target) &&
                !event.target.closest(".inner-dropdown-menu")
            ) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdown]);

    //Submit Single Handler
    const handleSubmitSingle = async (e) => {
        e.preventDefault();

        try {
            setIsSubmitSingle(true);
            setOpenDropdown(null);

            // Create FormData object
            const formData = new FormData();
            formData.append("template_id", templateId);
            formData.append("price_name", e.target.price_name.value || '');
            formData.append("content_heading", e.target.content_heading.value || '');
            formData.append("content_text", e.target.content_text.value || '');
            formData.append("content_promocode", e.target.content_promocode.value || '');
            formData.append("content_action_button", e.target.content_action_button.value || '');
            formData.append("content_action_link", e.target.content_action_link.value || '');
            formData.append("content_footer_text", e.target.content_footer_text.value || '');
            formData.append("setting_chance", chanceSize || '');
            formData.append("custom_color", customColor || '');

            //Api Call
            const response = await submitSingle(formData);
            
            if (response.status === true) { 
                toast.success(response.message);
                
                //Call templat details
                await fetchTemplateDetails();
                setActiveSettingElementsTab("main");
                setRefreshWidgetList(prev => !prev);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsSubmitSingle(false);
        }
    };

    // Handle Edit Click 
    const handleEditSingle = (data) => {
        setOpenDropdown(null);
        setSingleId(data.id);
        setActiveSettingElementsTab("edit_single");
        fetchSingleDetails(data.id);
    };

    // Handle Delete Click
    const handleDeleteSingle = async (data) => {
        setOpenDropdown(null);

        try {
            // Prepare the payload
            const payload = {
                single_id: data.id,
            };

            // API Call
            const api_response = await deleteSingle(payload);
            // Check API Response
            if (api_response.status === true) {
                toast.success(api_response.message);

                // Refresh testimonials
                await fetchTemplateDetails();
                setRefreshWidgetList(prev => !prev);
            } else {
                toast.error(api_response.message);
            }
        } catch (error) {
            toast.error(`${error.message}`);
        }
    };

    // Wrap fetchSingleDetails in useCallback to prevent re-creation on every render
    const fetchSingleDetails = useCallback(async (singleId) => {
        if (!singleId) return;

        setIsLoading(true); 
        try {
            const templateResult = await getSingleDetail(singleId); 
            if (templateResult?.status === true && templateResult?.data) {
                setApiSingle(templateResult.data);
            } else { 
                setApiSingle(null);
            }
        } catch (error) {
            //console.error("Error fetching:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Also include fetchTemplateDetails in this useEffect
    useEffect(() => {
        if (isEditPage && uuid && !apiSingle) {
            fetchSingleDetails(uuid);
        }
    }, [isEditPage, uuid, apiSingle, fetchSingleDetails]);

    //Update Single Handler
    const handleUpdateSingle = async (e) => {
        e.preventDefault();

        try {
            setIsSubmitSingle(true);
            setOpenDropdown(null);
            // Create FormData object
            const formData = new FormData();
            formData.append("id", singleId);
            formData.append("price_name", e.target.price_name?.value || '');
            formData.append("content_heading", e.target.content_heading?.value || '');
            formData.append("content_text", e.target.content_text?.value || '');
            formData.append("content_promocode", e.target.content_promocode?.value || '');
            formData.append("content_action_button", e.target.content_action_button?.value || '');
            formData.append("content_action_link", e.target.content_action_link?.value || '');
            formData.append("content_footer_text", e.target.content_footer_text?.value || '');
            formData.append("setting_chance", chanceSize || '');
            formData.append("custom_color", customColor || '');

            //Api Call
            const response = await updateSingle(formData);

            if (response.status) {
                toast.success(response.message);

                //Refresh Logo Showcase
                await fetchTemplateDetails();
                setRefreshWidgetList(prev => !prev);

                // Switch back to the main view
                setActiveSettingElementsTab("main");
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsSubmitSingle(false);
        }
    }; 

    //Useeffect for hanlde custom color and chance
    useEffect(() => {
        if (apiSingle && apiSingle.setting_color) {
            setCustomColor(apiSingle.setting_color);
        }
        if (apiSingle && typeof apiSingle.setting_chance === "number") {
            setChanceSize(apiSingle.setting_chance);
        }
    }, [apiSingle]);


    // Handlers Chance Size Change
    const handleChanceSizeChange = (e) => {
        const value = e.target.value;
        setChanceSize(value);
        setApiSingle(prev => ({ ...prev, setting_chance: value }));
    };

    // Handlers Custom Color Change
    const handleCustomColorChange = (color) => {
        setCustomColor(color.hex);
        setApiSingle(prev => ({ ...prev, setting_color: color.hex }));
    };

    //Handlers Heading Change
    const handleHeadingChange = (e) => {
        const value = e.target.value;
        setHeading(value);
        updateSpinningWheelTabData("heading", value); 
    };

    //Handlers Description Change
    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        setDescription(value);
        updateSpinningWheelTabData("description", value); 
    };

    //Handlers Show Email Change
    const handleShowEmailChange = (e) => {
        const checked = e.target.checked;
        setIsShowEmail(checked);
        updateSpinningWheelTabData("is_show_email", checked); 
    };

    //Handlers Footer Text Change
    const handleFooterTextChange = (e) => { 
        const value = e.target.value;
        setFooterText(value);
        updateSpinningWheelTabData("footer_text", value); 
    };

    //useEffect to load settings
    useEffect(() => {
        if (spinningWheelTabData) {
            setHeading(spinningWheelTabData.heading || "");
            setDescription(spinningWheelTabData.description || "");
            setIsShowEmail(Number(spinningWheelTabData.is_show_email) || 0);
            setFooterText(spinningWheelTabData.footer_text || "");
        }
    }, [spinningWheelTabData]);

    return (
        <div className="testimonials-side-baar-layout-tab">
            {activeSettingElementsTab === "main" && (
                <>
                    <span>Content</span>
                    <div className="side-baar-box-header">
                        <div className="box-header-row bubble-text">
                            <label>Heading</label>
                            <input name="heading" value={heading} onChange={handleHeadingChange} placeholder="Heading..." type="text" />
                        </div>
                        <div className="box-header-row bubble-text">
                            <label>Text</label>
                            <textarea
                                className="w-100"
                                name="description"
                                value={description}
                                onChange={handleDescriptionChange}
                            ></textarea>
                        </div>
                        <div className="box-header-row">
                            <span>Email Required to Spin</span>
                            <div className="form-group">
                                <input id="is_email_required" className="chkbx-toggle" type="checkbox" checked={isShowEmail} onChange={handleShowEmailChange} />
                                <label for="is_email_required"></label>
                            </div>
                        </div>
                        <div className="box-header-row bubble-text">
                            <label>Footer Text</label>
                            <textarea
                                className="w-100"
                                name="footer_text"
                                value={footerText}
                                onChange={handleFooterTextChange}
                            ></textarea>
                        </div>
                        <p>You can include prize terms, disclaimers, links to terms & conditions, privacy policy, etc.</p>
                    </div>
                    <div className="side-baar-box-header ">
                        <div className="box-header-row">
                            <span>Prizes</span>
                        </div>
                        <div className="testimonal-side-baar-list">
                            {isLoading ? (
                                <div className="inner-loader-container">
                                    <div className="inner-spinner"></div>
                                    <p>Loading, please wait...</p>
                                </div>
                            ) : apiProfileSociaMediaIcons?.length > 0 ? (
                                <ul className="">
                                    {apiProfileSociaMediaIcons.map((data, index) => (
                                        <li key={index} className="testimonial-item">
                                            <button type="button" className="testimonial-item-button" onClick={() => handleEditSingle(data)}>
                                                <div className="list-text-testimonal">  
                                                    <p className="pb-0">{data.price_name}</p>
                                                </div>
                                            </button>
                                            <div className="list-button-testimonal top-50 translate-middle-y" ref={(el) => (dropdownRefs.current[index] = el)}>
                                                <button className="position-static" onClick={() => toggleDropdown(index)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M3.864 8.333a1.363 1.363 0 1 1 0 2.726 1.363 1.363 0 0 1 0-2.726Zm6.136 0a1.363 1.363 0 1 1 .001 2.726A1.363 1.363 0 0 1 10 8.333Zm6.136 0a1.363 1.363 0 1 1 .001 2.726 1.363 1.363 0 0 1 0-2.726Z"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                            {openDropdown === index && (
                                                <div className="inner-dropdown-menu top-50">
                                                    <div className="button-list">
                                                        <button type="button" onClick={() => handleEditSingle(data)}>Edit</button>
                                                    </div>
                                                    <div className="button-list">
                                                        <button type="button" onClick={() => handleDeleteSingle(data)}> Delete</button>
                                                    </div>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className="add-testimonal-button">
                            <button onClick={() => setActiveSettingElementsTab("add_single")}>+ Add Prices</button>
                        </div>
                    </div>
                </>
            )}

            {activeSettingElementsTab === "add_single" && (
                <div className="inner-box-layout">
                    <form onSubmit={handleSubmitSingle}>
                        <span>
                            <button className="button-color" type="button" onClick={() => setActiveSettingElementsTab("main")}>
                                <i className="fa fa-angle-left" aria-hidden="true"></i>Cancel
                            </button>
                            Add Prize
                        </span>

                        <div className="side-baar-box-header">
                            <div className="box-header-row bubble-text">
                                <label>Prize Name</label>
                                <input name="price_name" placeholder="Name..." type="text" />
                            </div>
                        </div>
                        <div className="side-baar-box-header">
                            <p>Content</p>
                            <div className="box-header-row bubble-text">
                                <label>Heading</label>
                                <input name="content_heading" placeholder="Heading..." type="text" />
                            </div>
                            <div className="box-header-row bubble-text">
                                <label>Text</label>
                                <input name="content_text" placeholder="Text..." type="text" />
                            </div>
                            <div className="box-header-row bubble-text">
                                <label>Promocode</label>
                                <input name="content_promocode" placeholder="Promocode..." type="text" />
                            </div>
                        </div>

                        <div className="side-baar-box-header">
                            <div className="box-header-row bubble-text">
                                <label>Action Button Text</label>
                                <input name="content_action_button" placeholder="Action Button Text..." type="text" />
                            </div>
                            <div className="box-header-row bubble-text">
                                <label>Action Button Link</label>
                                <input name="content_action_link" placeholder="Action Button Link..." type="text" />
                            </div>
                            <div className="box-header-row bubble-text">
                                <label>Footer Text</label>
                                <textarea
                                    className="w-100"
                                    name="content_footer_text"
                                ></textarea>
                            </div>
                            <p>You can include prize terms, disclaimers, links to terms & conditions, privacy policy, etc.</p>
                        </div>
                        <div className="side-baar-box-header">
                            <p>Settings</p>
                            <div className="box-header-row">
                                <span>Custom Color</span>
                                <div className="color-pikr-box">
                                    <ColorPicker color={customColor} setColor={setCustomColor} onChangeComplete={handleCustomColorChange}/>
                                </div>
                            </div>
                            <div className="box-header-row">
                                <span>Chance</span>
                                <em>{chanceSize}%</em>
                                <div className="slider-container">
                                    <input type="range" min="0" max="100" value={chanceSize} onChange={handleChanceSizeChange}/>
                                </div>
                            </div>
                        </div>
                        <div className="side-baar-box-header">
                            <div className="customize-button mb-2"> 
                                <button type="submit" className="float-none text-black bg-white" disabled={isSubmitSingle}>
                                    {isSubmitSingle ? "Submiting..." : "Submit"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}

            {activeSettingElementsTab === "edit_single" && (
                <div className="inner-box-layout">
                    {isLoading ? (
                        <div className="inner-loader-container">
                            <div className="inner-spinner"></div>
                            <p>Loading, please wait...</p>
                        </div>
                    ) : apiSingle ? (
                        <>
                        <form onSubmit={handleUpdateSingle}>
                            <span>
                                <button className="button-color" type="button" onClick={() => setActiveSettingElementsTab("main")}>
                                    <i className="fa fa-angle-left" aria-hidden="true"></i>Cancel
                                </button>
                                Edit Icon
                            </span>

                            <div className="side-baar-box-header">
                                <div className="box-header-row bubble-text">
                                    <label>Prize Name</label>
                                    <input name="price_name" value={apiSingle.price_name || ""} onChange={(e) => setApiSingle({ ...apiSingle, price_name: e.target.value })} placeholder="Name..." type="text" />
                                </div>
                            </div>
                            <div className="side-baar-box-header">
                                <p>Content</p>
                                <div className="box-header-row bubble-text">
                                    <label>Heading</label>
                                    <input name="content_heading" value={apiSingle.content_heading || ""} onChange={(e) => setApiSingle({ ...apiSingle, content_heading: e.target.value })} placeholder="Heading..." type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <label>Text</label>
                                    <input name="content_text" value={apiSingle.content_text || ""} onChange={(e) => setApiSingle({ ...apiSingle, content_text: e.target.value })} placeholder="Text..." type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <label>Promocode</label>
                                    <input name="content_promocode" value={apiSingle.content_promocode || ""} onChange={(e) => setApiSingle({ ...apiSingle, content_promocode: e.target.value })} placeholder="Promocode..." type="text" />
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                                <div className="box-header-row bubble-text">
                                    <label>Action Button Text</label>
                                    <input name="content_action_button" value={apiSingle.content_action_button || ""} onChange={(e) => setApiSingle({ ...apiSingle, content_action_button: e.target.value })} placeholder="Action Button Text..." type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <label>Action Button Link</label>
                                    <input name="content_action_link" value={apiSingle.content_action_link || ""} onChange={(e) => setApiSingle({ ...apiSingle, content_action_link: e.target.value })}placeholder="Action Button Link..." type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <label>Footer Text</label>
                                    <textarea
                                        className="w-100"
                                        name="content_footer_text"
                                        value={apiSingle.content_footer_text || ""}
                                        onChange={(e) =>
                                            setApiSingle({ ...apiSingle, content_footer_text: e.target.value })
                                        }
                                    ></textarea>
                                </div>
                                <p>You can include prize terms, disclaimers, links to terms & conditions, privacy policy, etc.</p>
                            </div>

                            <div className="side-baar-box-header">
                                <p>Settings</p>
                                <div className="box-header-row">
                                    <span>Custom Color</span>
                                    <div className="color-pikr-box">
                                        <ColorPicker color={customColor} setColor={setCustomColor} onChangeComplete={handleCustomColorChange}/>
                                    </div>
                                </div>
                                <div className="box-header-row">
                                    <span>Chance</span>
                                    <em>{chanceSize}%</em>
                                    <div className="slider-container">
                                        <input type="range" min="0" max="100" value={apiSingle.setting_chance || ""} onChange={handleChanceSizeChange}/>
                                    </div>
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                                <div className="customize-button mb-2"> 
                                    <button type="submit" className="float-none text-black bg-white" disabled={isSubmitSingle}>
                                        {isSubmitSingle ? "Updating..." : "Update"}
                                    </button>
                                 </div>
                            </div>
                        </form>
                        </>
                    ) : (
                        <p>No single details. Please try again.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContentTab;